import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { useTheme } from "../components/ThemeProvider";
import { mostrarToastVitalCasa } from "../utils/mostrarToastVitalCasa";

function botonBarra(label, onClick, extra) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #334155",
        background: extra?.primary ? "#2563eb" : "#1e293b",
        color: "#fff",
        fontWeight: 600,
        fontSize: 13,
        cursor: extra?.disabled ? "wait" : "pointer",
        opacity: extra?.disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
}

export default function PaginaEncargoGenerado() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { theme } = useTheme();
  const c = theme?.colors || {};
  const iframeRef = useRef(null);
  const blobUrlRef = useRef("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);
  const [modalVf, setModalVf] = useState(false);
  const [owners, setOwners] = useState([]);
  const [canales, setCanales] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [ampliado, setAmpliado] = useState(false);

  const tipo = sp.get("tipo") || "Encargo de Venta";
  const soloFechaFin = sp.get("solo_fecha_fin") === "1";
  const duracion = soloFechaFin ? "" : (sp.get("duracion") || "");
  const desde = sp.get("desde") || "";
  const hasta = sp.get("hasta") || "";
  const precio = sp.get("precio") || "";
  const precioVenta = sp.get("precio_venta") || precio || "";
  const precioPropietario = sp.get("precio_propietario") || "";
  const honorariosEuros = sp.get("honorarios_euros") || "";
  const ivaEuros = sp.get("iva_euros") || "";
  const honorariosPct = sp.get("honorarios_porcentaje") || "";
  const totalHonorarios = sp.get("total_honorarios") || "";
  const ivaPct = sp.get("iva_porcentaje") || "";
  const motivos = String(sp.get("motivos") || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

  const bodyPreview = useMemo(() => ({
    propiedad_id: id,
    tipo_encargo: tipo,
    exclusiva: /exclusiva/i.test(tipo),
    solo_fecha_fin: soloFechaFin || undefined,
    exclusiva_duracion: duracion || undefined,
    exclusiva_desde: desde || undefined,
    exclusiva_hasta: hasta || undefined,
    // PDF: mediacional → propietario; % incluido → venta (ya resuelto en `precio`).
    precio: precio || precioPropietario || precioVenta || undefined,
    precio_venta: precioVenta || undefined,
    precio_propietario: precioPropietario || undefined,
    encargo_pendiente: {
      motivos,
      precio_venta: precioVenta || undefined,
      precio_propietario: precioPropietario || undefined,
      honorarios_euros: honorariosEuros || undefined,
      iva_euros: ivaEuros || undefined,
      honorarios_porcentaje: honorariosPct || undefined,
      total_honorarios: totalHonorarios || undefined,
      iva_porcentaje: ivaPct || undefined,
      tipo_encargo: tipo,
      exclusiva: /exclusiva/i.test(tipo),
      // Mismo tipo: no cambiar duración/meses en ficha; solo fecha fin ya vigente.
      exclusiva_duracion: soloFechaFin ? undefined : (duracion || undefined),
      exclusiva_desde: desde || undefined,
      exclusiva_hasta: hasta || undefined,
    },
  }), [
    id, tipo, soloFechaFin, duracion, desde, hasta, precio, precioVenta, precioPropietario,
    honorariosEuros, ivaEuros, honorariosPct, totalHonorarios, ivaPct, motivos.join(","),
  ]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setCargando(true);
      setError("");
      try {
        const r = await api.post("/viafirma/preview-encargo-propiedad", bodyPreview, {
          responseType: "blob",
        });
        const blob = new Blob([r.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        if (alive) setPdfUrl(url);
      } catch (e) {
        let msg = "No se pudo generar el encargo";
        const data = e.response?.data;
        if (data instanceof Blob) {
          try {
            const t = JSON.parse(await data.text());
            if (t?.error) msg = t.error;
          } catch { /* ignore */ }
        } else if (data?.error) msg = data.error;
        else if (e.message) msg = e.message;
        if (alive) setError(msg);
      } finally {
        if (alive) setCargando(false);
      }
    })();
    return () => {
      alive = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = "";
      }
    };
  }, [bodyPreview]);

  const guardar = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `encargo-${tipo.replace(/\s+/g, "-")}.pdf`;
    a.click();
  };

  const imprimir = () => {
    const w = iframeRef.current?.contentWindow;
    if (w) {
      w.focus();
      w.print();
      return;
    }
    window.print();
  };

  const ampliar = async () => {
    const el = document.getElementById("vc-encargo-visor");
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setAmpliado(true);
      } else {
        await document.exitFullscreen();
        setAmpliado(false);
      }
    } catch {
      setAmpliado((v) => !v);
    }
  };

  const abrirViaFirma = async () => {
    try {
      const r = await api.get(`/viafirma/datos-encargo-propiedad/${id}`);
      const list = r.data?.propietarios || [];
      if (!list.length) {
        mostrarToastVitalCasa("No hay propietarios en la ficha", { tipo: "error" });
        return;
      }
      const next = {};
      list.forEach((p) => {
        next[p.key] = p.email ? "email" : (p.telefono ? "dispositivo" : "email");
      });
      setOwners(list);
      setCanales(next);
      setModalVf(true);
    } catch (e) {
      mostrarToastVitalCasa(e.response?.data?.error || "No se pudieron cargar los firmantes", { tipo: "error" });
    }
  };

  const enviarViaFirma = async () => {
    setEnviando(true);
    try {
      const firmantes = owners.map((p) => ({
        nombre: p.nombre,
        dni: p.dni,
        email: p.email,
        telefono: p.telefono,
        canal: canales[p.key] || "email",
        ownerKey: p.key,
      }));
      const r = await api.post("/viafirma/enviar-encargo-propiedad", {
        ...bodyPreview,
        firmantes,
        encargo_pendiente: bodyPreview.encargo_pendiente,
      });
      setModalVf(false);
      mostrarToastVitalCasa(r.data?.message || "Encargo enviado por ViaFirma");
      const url = r.data?.signing_url;
      if (url) window.open(url, "vc_viafirma");
    } catch (e) {
      mostrarToastVitalCasa(e.response?.data?.error || "Error al enviar por ViaFirma", { tipo: "error" });
    } finally {
      setEnviando(false);
    }
  };

  const onFs = useCallback(() => {
    setAmpliado(!!document.fullscreenElement);
  }, []);
  useEffect(() => {
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [onFs]);

  return (
    <div id="vc-encargo-visor" style={{ minHeight: "100vh", background: c?.bgApp || "#0f172a", color: "#fff", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "10px 14px", background: "#0b1220", borderBottom: "1px solid #1e293b" }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginRight: 8 }}>{tipo}</div>
        {botonBarra("Guardar", guardar)}
        {botonBarra("Imprimir", imprimir)}
        {botonBarra(ampliado ? "Salir ampliar" : "Ampliar", ampliar)}
        {botonBarra("Enviar por ViaFirma", abrirViaFirma, { primary: true })}
      </div>
      <div style={{ flex: 1, minHeight: 0, background: "#111827" }}>
        {cargando && <p style={{ padding: 24, color: "#94a3b8" }}>Generando encargo…</p>}
        {error && <p style={{ padding: 24, color: "#fca5a5" }}>{error}</p>}
        {pdfUrl && !error && (
          <iframe
            ref={iframeRef}
            title="Encargo generado"
            src={pdfUrl}
            style={{ width: "100%", height: "calc(100vh - 56px)", border: 0, background: "#111" }}
          />
        )}
      </div>
      {modalVf && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => !enviando && setModalVf(false)}
        >
          <div style={{ background: "#fff", color: "#0f172a", borderRadius: 14, padding: 18, width: "100%", maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Enviar por ViaFirma</div>
            {owners.map((p) => (
              <div key={p.key} style={{ marginBottom: 12, borderBottom: "1px solid #e2e8f0", paddingBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.nombre}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{p.email || "sin email"} · {p.telefono || "sin teléfono"}</div>
                <select
                  value={canales[p.key] || "email"}
                  onChange={(e) => setCanales((prev) => ({ ...prev, [p.key]: e.target.value }))}
                  style={{ marginTop: 6, width: "100%", padding: 8, borderRadius: 8, border: "1px solid #cbd5e1" }}
                >
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="dispositivo">Este dispositivo</option>
                </select>
              </div>
            ))}
            <button type="button" disabled={enviando} onClick={enviarViaFirma} style={{ width: "100%", padding: 10, borderRadius: 8, border: 0, background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              {enviando ? "Enviando…" : "Enviar"}
            </button>
            <button type="button" disabled={enviando} onClick={() => setModalVf(false)} style={{ marginTop: 8, width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
